/**
 * Existing Debt Configuration Constants
 * Centralized source of truth for existing debt validation and UI constraints.
 *
 * Related files:
 * - apps/web/src/builders/existing-debt/ExistingDebtInputs.tsx
 * - apps/web/src/builders/existing-debt/ExistingDebtBuilder.ts
 * - apps/web/src/utils/reverseAmortization.ts
 */

// =============================================================================
// VALIDATION CONSTRAINTS
// =============================================================================

export const EXISTING_DEBT_VALIDATION = {
  /**
   * Maximum principal payment value (in dollars)
   * Used to prevent auto-multiply from creating unreasonably large values
   * Rationale: 20 million is a reasonable upper bound for municipal debt payments
   */
  MAX_PRINCIPAL_VALUE: 20_000_000,

  /**
   * Auto-multiply factor for principal payments
   * Users enter values in thousands, we multiply on blur to get actual amount
   * Example: User enters "1610" → stored as "1610000"
   */
  AUTO_MULTIPLY_FACTOR: 1000,

  /**
   * Minimum required fields for valid debt input
   */
  REQUIRED_FIELDS: {
    ID: 'id',
    NAME: 'name',
    PAYMENTS: 'payments',
  },
} as const;

// =============================================================================
// UI CONFIGURATION
// =============================================================================

export const EXISTING_DEBT_UI = {
  /**
   * Month numbers for date filtering
   * Michigan bond payments typically occur in May and November
   */
  MONTHS: {
    MAY: 5,
    NOVEMBER: 11,
  },

  /**
   * Keyboard navigation column indices
   * Used for arrow key navigation between table cells
   */
  COLUMNS: {
    INTEREST_RATE: 1,
    PRINCIPAL_PAYMENT: 2,
  },

  /**
   * Default auto-multiply setting
   * Most users prefer entering values in thousands
   */
  DEFAULT_AUTO_MULTIPLY_ENABLED: true,

  /**
   * Default lock states for date filtering
   */
  DEFAULT_LOCK_MAY_DATES: false,
  DEFAULT_LOCK_NOVEMBER_DATES: false,
} as const;

// =============================================================================
// CALCULATION SETTINGS
// =============================================================================

export const EXISTING_DEBT_CALCULATIONS = {
  /**
   * Interest calculation method
   * Municipal bonds use 30/360 day count convention
   * Semi-annual payments = 180 days out of 360
   */
  DAYS_PER_SEMI_ANNUAL_PERIOD: 180,
  DAYS_PER_YEAR: 360,

  /**
   * Semi-annual interest rate divisor
   * Annual rate / 2 for semi-annual payments
   */
  SEMI_ANNUAL_DIVISOR: 2,

  /**
   * Tolerance for floating point comparisons
   * Used when comparing calculated vs existing interest values
   */
  FLOAT_COMPARISON_TOLERANCE: 0.01,
} as const;

// =============================================================================
// TYPE GUARDS AND HELPERS
// =============================================================================

/**
 * Check if a month number represents May
 */
export const isMay = (month: number): boolean => {
  return month === EXISTING_DEBT_UI.MONTHS.MAY;
};

/**
 * Check if a month number represents November
 */
export const isNovember = (month: number): boolean => {
  return month === EXISTING_DEBT_UI.MONTHS.NOVEMBER;
};

/**
 * Extract month from YYYY-MM-DD date string
 * Returns month as 1-indexed number (1-12)
 */
export const getMonthFromDateString = (dateString: string): number => {
  const monthStr = dateString.substring(5, 7);
  return parseInt(monthStr, 10);
};
